import { Dialog, Transition } from "@headlessui/react";
import cx from "classnames";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useRouter } from "next/router";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Close } from "@/components/icons/close";
import endpoints from "@/utils/endpoints";

const get = endpoints();

export type RegistrationWidgetProps = {
  isOpen?: boolean;
  onClose: () => void;
  eventSlug?: string;
  seriesExternalId?: string;
};

const RegistrationWidget = ({
  isOpen = false,
  onClose,
  eventSlug,
  seriesExternalId,
}: RegistrationWidgetProps) => {
  const {
    query: { slug },
  } = useRouter();

  const flags = useFlags();
  const { t } = useTranslation();

  const isV2Layout = flags.registrationFormLayoutV2;

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("registration-modal-open");

      const ot = window?.OneTrust;

      if (ot && !ot.IsAlertBoxClosed()) {
        ot.Close();
      }
    } else {
      document.body.classList.remove("registration-modal-open");
    }
  }, [isOpen]);

  const iframeUrl = `${get("REGISTRATION_WIDGET")(
    (eventSlug || slug) as string
  )}${
    window.location.search
      ? window.location.search +
        `&widget_source=canvas${
          seriesExternalId ? `&series_external_id=${seriesExternalId}` : ""
        }`
      : `?widget_source=canvas${
          seriesExternalId ? `&series_external_id=${seriesExternalId}` : ""
        }`
  }`;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        className="fixed inset-0 z-50"
        onClose={() => {
          return; // prevent dialog from closing when clicking outside
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0  bg-[#000a26]/60 backdrop-blur-sm"
            aria-hidden="true"
          />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fill-available relative flex items-center justify-center">
            <Dialog.Panel
              className={cx(
                "relative m-auto w-full overflow-hidden bg-white fill-available",
                {
                  "md:h-[45rem] md:max-w-[61.5rem] md:rounded-2xl [@media(max-height:45rem)]:md:h-full [@media(max-height:45rem)]:md:rounded-none": isV2Layout,
                }
              )}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close")}
                className={cx(
                  "absolute z-[100] flex size-8 items-center justify-center rounded-full bg-[#000a26]/60 text-white transition-colors hover:bg-black/30 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/20",
                  {
                    "right-5 top-4": isV2Layout,
                    "right-6 top-6": !isV2Layout,
                  }
                )}
              >
                <Close className="size-5" />
              </button>
              <iframe
                title="Registration Widget"
                src={iframeUrl}
                data-testid="registration-widget"
                className="absolute inset-0 size-full border-none"
              />
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default RegistrationWidget;
